import { DateThreshold } from 'components/Input/variations/Calendar/types'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import React from 'react'

type DateCellRenderProps = {
  date: Dayjs
  firstValue: string
  setFirstValue: React.Dispatch<React.SetStateAction<string>>
  secondValue: string
  setSecondValue: React.Dispatch<React.SetStateAction<string>>
  dateThreshold: DateThreshold
}

function getRange(firstValue: string, secondValue: string) {
  const firstValueTime = new Date(firstValue).getTime()
  const secondValueTime = new Date(secondValue).getTime()

  return {
    start: Math.min(firstValueTime, secondValueTime),
    end: Math.max(firstValueTime, secondValueTime)
  }
}

function isInRange(firstValue: string, secondValue: string, date: Dayjs) {
  const { start, end } = getRange(firstValue, secondValue)
  const dateMs = getDateTime(getDateStringFormat(date))

  return {
    isStart: start === dateMs,
    isEnd: end === dateMs,
    inRange: dateMs > start && dateMs < end
  }
}

function getDateStringFormat(date: Dayjs) {
  return date.format('YYYY-MM-DD')
}

function getDateTime(date: string) {
  return new Date(date).getTime()
}

function handleOnChange(
  date: Dayjs, 
  firstValue: string, 
  secondValue: string, 
  setFirstValue: React.Dispatch<React.SetStateAction<string>>, 
  setSecondValue: React.Dispatch<React.SetStateAction<string>>,
  threshold: DateThreshold,
) {
  if(date.isBefore(threshold.start) || date.isAfter(threshold.end)) {
    return
  }

  if (!firstValue) {
    return setFirstValue(getDateStringFormat(date))
  }

  if (!secondValue) {
    return setSecondValue(getDateStringFormat(date))
  }

  setFirstValue(getDateStringFormat(date))
  setSecondValue('')
}

export default function DateCellRender({ date, firstValue, setFirstValue, secondValue, setSecondValue, dateThreshold }: DateCellRenderProps) {
  const isToday = getDateStringFormat(date) === getDateStringFormat(dayjs(new Date()))
  let dateCellClass = 'default-date-cell'

  if ((firstValue && !secondValue) && getDateTime(getDateStringFormat(date)) === new Date(firstValue).getTime()) {
    dateCellClass += ' date-selected'
  }

  if(firstValue && secondValue) {
    const { isStart, isEnd, inRange } = isInRange(firstValue, secondValue, date)

    if(isStart || isEnd) {
      dateCellClass += ' date-selected'
      dateCellClass += isStart ? ' start' : ' end'
    }

    if (inRange) {
      dateCellClass += ' date-in-range'
    }
  }

  if (isToday) {
    dateCellClass += ' today-date-cell'
  }

  return (
    <div 
      className='calendar-date-cell'
      onClick={() => handleOnChange(date, firstValue, secondValue, setFirstValue, setSecondValue, dateThreshold)}>
      <p className={dateCellClass}>
        {date.date()}
      </p>
    </div>
  )
}
